import React, { useState, useRef, useEffect } from 'react';
import { CButton, CFormSelect, CFormInput, CSpinner } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSend } from '@coreui/icons';
import { getAuth } from 'firebase/auth';
import config from '../../utils/EnvironmentSwitcher';
import { enqueueAgentChatTask, waitForAgentTask } from '../../services/AgentTaskService';
import { ensureFhirUserIdentifier } from '../../utils/fhirIdentifier';

const SCHEDULER_SYSTEM_HINT = `
If the user asks to set up a reminder, schedule, recurring check, or timed follow-up, include one JSON block in triple backticks:
\`\`\`json
{
  "assistant_reply": "short plain-language reply",
  "schedule_offer": {
    "title": "string",
    "description": "string",
    "dueTime": "ISO-8601 date-time",
    "category": "reminder"
  }
}
\`\`\`
If the user confirms a previous offer (e.g. "yes"), include:
\`\`\`json
{
  "assistant_reply": "short plain-language confirmation",
  "schedule_create": {
    "title": "string",
    "description": "string",
    "dueTime": "ISO-8601 date-time",
    "category": "reminder"
  }
}
\`\`\`
For recurring schedules, include a recurrence object:
\`\`\`json
{
  "assistant_reply": "short plain-language reply",
  "schedule_offer": {
    "title": "string",
    "description": "string",
    "dueTime": "ISO-8601 date-time",
    "category": "reminder",
    "recurrence": {
      "frequency": "daily|weekly|monthly",
      "interval": 1
    }
  }
}
\`\`\`
`;

const toAgentList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.agents)) return data.agents;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const extractContent = (task, fallback = 'No response.') => {
  const data = task?.result || {};
  return (
    data.reply ||
    data.output ||
    data.summary ||
    data.choices?.[0]?.message?.content ||
    data.error ||
    (typeof data === 'string' ? data : null) ||
    fallback
  );
};

const extractJsonDirective = (text) => {
  if (!text || typeof text !== 'string') return null;
  const fenced = text.match(/```json\s*([\s\S]*?)```/i);
  const source = fenced?.[1] || null;
  if (!source) return null;
  try {
    return JSON.parse(source);
  } catch {
    return null;
  }
};

const stripJsonDirective = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/```json[\s\S]*?```/gi, '').trim();
};

const parseRelativeDueTime = (sourceText = '', now = new Date()) => {
  const text = String(sourceText || '').toLowerCase();
  if (!text) return null;

  const rel = text.match(/\b(?:in\s+)?(\d+)\s*(minute|minutes|min|hour|hours|day|days)\b/i);
  if (rel) {
    const value = Number(rel[1]);
    const unit = rel[2].toLowerCase();
    const d = new Date(now);
    if (unit.startsWith('min')) d.setMinutes(d.getMinutes() + value);
    else if (unit.startsWith('hour')) d.setHours(d.getHours() + value);
    else if (unit.startsWith('day')) d.setDate(d.getDate() + value);
    return d.toISOString();
  }

  if (/\b(in|after)\s+(a|one)\s+minute\b/i.test(text)) {
    const d = new Date(now);
    d.setMinutes(d.getMinutes() + 1);
    return d.toISOString();
  }

  return null;
};

const resolveDueTime = (dueTimeRaw, sourceText = '') => {
  const now = new Date();
  const parsed = dueTimeRaw ? new Date(dueTimeRaw) : null;
  const parsedValid = parsed && !Number.isNaN(parsed.getTime());
  const parsedFuture = parsedValid && parsed.getTime() > now.getTime() - 60 * 1000;
  if (parsedFuture) return parsed.toISOString();

  const relative = parseRelativeDueTime(sourceText, now);
  if (relative) return relative;

  const fallback = new Date(now);
  fallback.setMinutes(fallback.getMinutes() + 1);
  return fallback.toISOString();
};

const SidebarGptWidget = ({ isCollapsed }) => {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [lastAgentTaskId, setLastAgentTaskId] = useState(null);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId) || null;

  const fetchAgents = async () => {
    try {
      setAgentsLoading(true);
      setError('');
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setAgents([]);
        setSelectedAgentId('');
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(`${config.apiUrl}/api/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch agents (${res.status})`);
      const data = await res.json();
      const list = toAgentList(data);
      setAgents(list);
      setSelectedAgentId((prev) => prev || list[0]?.id || '');
    } catch (err) {
      console.error('SidebarGptWidget: fetchAgents error', err);
      setError(err.message || 'Failed to load agents');
      setAgents([]);
      setSelectedAgentId('');
    } finally {
      setAgentsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    setChatMessages([]);
    setActiveConversationId(null);
    setChatInput('');
    setError('');
  }, [selectedAgentId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading, scheduleLoading]);

  const createFHIRTaskFromSchedule = async (scheduleData) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser?.uid) {
      throw new Error('Missing authenticated user UID for FHIR Task identifier');
    }
    const token = await currentUser.getIdToken();
    const title = (scheduleData?.title || 'Scheduled reminder').trim();
    const description = (scheduleData?.description || '').trim();
    const dueTime = resolveDueTime(scheduleData?.dueTime, scheduleData?._sourceUserText || '');
    const linkAgentTaskId = scheduleData?._agentTaskId || null;
    const linkConversationId = scheduleData?._conversationId || null;

    const taskResource = ensureFhirUserIdentifier(
      {
        resourceType: 'Task',
        status: 'requested',
        intent: 'order',
        priority: 'routine',
        description: title,
        executionPeriod: { end: dueTime },
        authoredOn: new Date().toISOString(),
        identifier: [
          { system: 'https://fhir.mieros.cloud/fhir/notes', value: `${selectedAgentId}-${Date.now()}` },
          { system: 'https://fhir.mieros.cloud/fhir/agent-id', value: String(selectedAgentId) },
          { system: 'https://fhir.mieros.cloud/fhir/ids', value: currentUser.uid },
          ...(linkAgentTaskId
            ? [{ system: 'https://fhir.mieros.cloud/fhir/agent-task-id', value: String(linkAgentTaskId) }]
            : []),
          ...(linkConversationId
            ? [{ system: 'https://fhir.mieros.cloud/fhir/agent-conversation-id', value: String(linkConversationId) }]
            : []),
        ],
        code: { text: scheduleData?.category || 'reminder' },
        extension: scheduleData?.recurrence
          ? [
              {
                url: 'https://fhir.mieros.cloud/fhir/extensions/recurrence',
                valueString: JSON.stringify(scheduleData.recurrence),
              },
            ]
          : [],
        note: description ? [{ text: description }] : [],
      },
      currentUser.uid,
    );

    const response = await fetch(`${config.fhirUrl}/fhir/Task`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/fhir+json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(taskResource),
    });

    if (!response.ok) {
      throw new Error(`Failed to create scheduled task (${response.status})`);
    }

    const created = await response.json();
    return { ...created, ...scheduleData, dueTime, taskId: created?.id };
  };

  const createScheduleFromOffer = async (scheduleData, sourceText = '') => {
    setScheduleLoading(true);
    try {
      const created = await createFHIRTaskFromSchedule({
        ...(scheduleData || {}),
        _sourceUserText: sourceText || scheduleData?._sourceUserText || '',
        _agentTaskId: scheduleData?._agentTaskId || lastAgentTaskId,
        _conversationId: scheduleData?._conversationId || activeConversationId,
      });
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "Got it. I've scheduled that for you." },
        { role: 'assistant', type: 'schedule_card', schedule: created },
      ]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Scheduling failed: ${err.message}` },
      ]);
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text || !selectedAgentId || chatLoading) return;

    const userMsg = { role: 'user', content: text };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    setError('');

    try {
      const lastScheduleOffer = [...chatMessages]
        .reverse()
        .find((msg) => msg.type === 'schedule_offer' && msg.scheduleOffer);
      if (lastScheduleOffer && /^(yes|yep|sure|ok|okay|do it)$/i.test(text)) {
        await createScheduleFromOffer(
          lastScheduleOffer.scheduleOffer,
          lastScheduleOffer.scheduleOffer?._sourceUserText || '',
        );
        setChatLoading(false);
        return;
      }

      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const historyForModel = chatMessages
        .filter((m) => typeof m?.content === 'string' && m.content.trim())
        .map((m) => ({ role: m.role, content: m.content }));
      const outbound = [{ role: 'system', content: SCHEDULER_SYSTEM_HINT }, ...historyForModel, userMsg];

      const queued = await enqueueAgentChatTask({
        token,
        agentId: selectedAgentId,
        messages: outbound,
        conversationId: activeConversationId,
      });
      const taskId = queued.task_id || queued.taskId;
      if (!taskId) throw new Error('Task queued but no task ID returned');

      const task = await waitForAgentTask({ token, taskId });
      setLastAgentTaskId(taskId);
      if (task.status === 'failed') {
        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', content: task.error || 'Task failed.' },
        ]);
        return;
      }

      const data = task.result || {};
      const taskConversationId = data.conversation_id || activeConversationId || taskId;
      if (taskConversationId && taskConversationId !== activeConversationId) {
        setActiveConversationId(taskConversationId);
      }

      const rawAssistantContent = extractContent(task);
      const directive = extractJsonDirective(rawAssistantContent);
      const assistantContent =
        directive?.assistant_reply || stripJsonDirective(rawAssistantContent) || 'No response.';

      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: assistantContent },
      ]);

      if (directive?.schedule_offer) {
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            type: 'schedule_offer',
            scheduleOffer: {
              ...directive.schedule_offer,
              _sourceUserText: text,
              _agentTaskId: taskId,
              _conversationId: taskConversationId,
            },
          },
        ]);
      }

      if (directive?.schedule_create) {
        await createScheduleFromOffer(
          {
            ...directive.schedule_create,
            _agentTaskId: taskId,
            _conversationId: taskConversationId,
          },
          text,
        );
      }
    } catch (err) {
      console.error('SidebarGptWidget: chat error', err);
      const message = err.message || 'Failed to send message';
      setError(message);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${message}` },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="sidebar-gpt-widget">
      <div className="mb-2">MiE GPT Agent Chat</div>

      <div className="d-flex flex-column gap-2">
        {!isCollapsed && (
          <CFormSelect
            size="sm"
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            disabled={agentsLoading}
          >
            {agentsLoading && <option value="">Loading agents...</option>}
            {!agentsLoading && agents.length === 0 && <option value="">No agents available</option>}
            {!agentsLoading &&
              agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name || agent.id}
                </option>
              ))}
          </CFormSelect>
        )}

        {!isCollapsed && (
          <div
            style={{
              minHeight: 120,
              maxHeight: 180,
              overflowY: 'auto',
              background: 'rgba(0,0,0,0.15)',
              borderRadius: 10,
              padding: 8,
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {chatMessages.length === 0 && (
              <div className="mb-2 small text-center" style={{ paddingTop: 36 }}>
                {selectedAgent ? `Chat with ${selectedAgent.name}` : 'Select an agent'}
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div
                key={`${msg.role}-${i}`}
                className={`d-flex mb-1 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
              >
                {msg.type === 'schedule_offer' ? (
                  <div
                    style={{
                      maxWidth: '86%',
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: 'rgba(255,255,255,0.08)',
                      color: 'var(--text-primary)',
                      fontSize: '0.76rem',
                      lineHeight: 1.35,
                    }}
                  >
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>
                      {msg.scheduleOffer?.title || 'Schedule suggestion'}
                    </div>
                    <div style={{ marginBottom: 6, opacity: 0.9 }}>
                      {msg.scheduleOffer?.description || ''}
                    </div>
                    <div style={{ marginBottom: 8, fontSize: '0.72rem', opacity: 0.75 }}>
                      {msg.scheduleOffer?.dueTime ? new Date(msg.scheduleOffer.dueTime).toLocaleString() : 'Time not provided'}
                    </div>
                    <CButton
                      size="sm"
                      color="info"
                      variant="outline"
                      disabled={scheduleLoading}
                      onClick={() =>
                        createScheduleFromOffer(
                          msg.scheduleOffer,
                          msg.scheduleOffer?._sourceUserText || '',
                        )
                      }
                    >
                      {scheduleLoading ? 'Scheduling...' : 'Schedule this'}
                    </CButton>
                  </div>
                ) : msg.type === 'schedule_card' ? (
                  <div
                    style={{
                      maxWidth: '86%',
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: 'rgba(0, 123, 255, 0.14)',
                      color: 'var(--text-primary)',
                      fontSize: '0.76rem',
                      lineHeight: 1.35,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {msg.schedule?.title || msg.schedule?.description || 'Scheduled action'}
                    </div>
                    <div style={{ opacity: 0.9 }}>
                      {msg.schedule?.description || 'Reminder created'}
                    </div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.75 }}>
                      {msg.schedule?.dueTime ? new Date(msg.schedule.dueTime).toLocaleString() : ''}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      maxWidth: '82%',
                      padding: '6px 8px',
                      borderRadius: 8,
                      fontSize: '0.78rem',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.35,
                      background:
                        msg.role === 'user'
                          ? 'linear-gradient(135deg, var(--electric-blue), var(--electric-purple))'
                          : 'rgba(255,255,255,0.08)',
                      color: msg.role === 'user' ? '#fff' : 'var(--text-primary)',
                    }}
                  >
                    {msg.content}
                  </div>
                )}
              </div>
            ))}
            {(chatLoading || scheduleLoading) && (
              <div className="d-flex justify-content-start mb-1">
                <div
                  style={{
                    padding: '6px 8px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.08)',
                  }}
                >
                  <CSpinner size="sm" /> {scheduleLoading ? ' Scheduling...' : ''}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}

        <div className="d-flex gap-1">
          <CFormInput
            size="sm"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder={isCollapsed ? 'Message...' : `Message ${selectedAgent?.name || 'agent'}...`}
            onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
            disabled={!selectedAgentId || chatLoading || scheduleLoading}
          />
          <CButton
            size="sm"
            color="primary"
            onClick={handleSendChat}
            disabled={!selectedAgentId || !chatInput.trim() || chatLoading || scheduleLoading}
          >
            <CIcon icon={cilSend} />
          </CButton>
        </div>

        {!!error && !isCollapsed && (
          <div className="text-danger small">{error}</div>
        )}

        {!isCollapsed && (
          <div className="mb-2 small">
            Mode: Agent Task Chat
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarGptWidget;


